import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, ArrowRight, Clock, MessageSquare, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function SuccessPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const steps = [
    { icon: FileText, title: "Request Received", desc: "Your project request has been logged in our system." },
    { icon: Clock, title: "Review in Progress", desc: "Our team will review your requirements within 24 hours." },
    { icon: MessageSquare, title: "We'll Reach Out", desc: "Expect a call or email with a detailed proposal and timeline." },
  ];

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      {/* Noise overlay */}
      <div className="noise-overlay" />
      
      {/* Grid lines */}
      <div className="grid-lines" />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative z-10 text-center">
        {/* Success Icon */}
        <div className="relative inline-block mb-8">
          <div className="absolute -inset-8 bg-[#FFD600]/20 blur-3xl rounded-full" />
          <div className="relative w-24 h-24 bg-[#121212] border-2 border-[#FFD600] flex items-center justify-center">
            <CheckCircle className="h-12 w-12 text-[#FFD600]" />
          </div>
        </div>

        {/* Heading */}
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4" data-testid="success-heading">
          Request <span className="text-[#FFD600]">Submitted!</span>
        </h1>
        <p className="text-[#A1A1AA] text-lg mb-12 max-w-md mx-auto" data-testid="success-message">
          Thank you for choosing MetDigital. We're excited to bring your vision to life.
        </p>

        {/* Timeline Steps */}
        <div className="bg-[#121212] border border-[#27272A] p-8 mb-12" data-testid="next-steps">
          <h2 className="font-heading text-xl font-bold text-[#3B82F6] mb-8">What Happens Next</h2>
          
          <div className="space-y-6">
            {steps.map((step, idx) => (
              <div key={idx} className="flex items-start gap-4 text-left" data-testid={`step-${idx}`}>
                <div className="flex-shrink-0 w-10 h-10 bg-[#0A0A0A] border border-[#27272A] flex items-center justify-center">
                  <step.icon className="h-5 w-5 text-[#FFD600]" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-white mb-1">{step.title}</h3>
                  <p className="text-[#A1A1AA] text-sm">{step.desc}</p>
                </div>
                {idx < steps.length - 1 && (
                  <div className="absolute left-9 mt-12 w-px h-6 bg-[#27272A]" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <Button
          onClick={() => navigate("/")}
          className="btn-primary bg-[#FFD600] text-black font-bold uppercase tracking-wider hover:bg-[#E6C200] rounded-none px-8 py-6 text-lg"
          data-testid="back-home-btn"
        >
          Back to Home <ArrowRight className="ml-2 h-5 w-5" />
        </Button>

        {/* Contact Info */}
        <p className="text-[#A1A1AA] text-sm mt-12">
          Questions? Contact us at{" "}
          <a href="mailto:info@metdigital.co.za" className="text-[#FFD600] hover:underline" data-testid="contact-email">
            info@metdigital.co.za
          </a>
        </p>
      </div>
    </div>
  );
}
